import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  createDining,
  getDiningByID,
  DiningImageUpload,
  updateDining,
  deleteDiningImage,
} from "./services/dining.service";
import {
  clearDiningDetails,
  clearDiningImageID,
  setDiningImageID,
} from "./diningSlice";
import { getFile } from "../../_helper/helper";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import DiningSpecialitiesArray from "./DiningSpecialitiesArray";
import { useLocation, useNavigate } from "react-router-dom";
import ShadowCard from "../../components/common/ShadowCard";
import IconComponent from "../../components/common/IconComponent";
import { MdDelete } from "react-icons/md";

const CreateDining: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const gridRef = useRef<any>(null);
  const DiningID = location.state?.DiningID;
  const DiningDetail = useAppSelector((state) => state.dining.DiningDetail);
  const loading = useAppSelector((state) => state.dining.formLoading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [DiningDetail]);

  useEffect(() => {
    if (DiningID) {
      dispatch(getDiningByID(DiningID));
    }
    return () => {
      dispatch(clearDiningDetails());
    };
  }, [DiningID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      DiningTitle: values.DiningTitle ?? "",
      DiningTagline: values.DiningTagline ?? "",
      DiningDescription: values.DiningDescription ?? "",
      Timing: values.Timing ?? "",
      Seating: values.Seating ? values.Seating + "" : "",
      Cuisine: values.Cuisine ?? "",
      SequenceNo: values.SequenceNo ?? 0,
      IsActive: values.IsActive === 1,
      CreatedBy: 2,
      UpdatedBy: 2,
      DiningSpecialities: values.DiningSpecialities ?? [],
    };

    try {
      let response;
      if (DiningID) {
        const editPayload = {
          ...formData,
          ID: DiningID,
          UpdatedBy: 3,
        };
        response = await dispatch(updateDining(editPayload));
      } else {
        response = await dispatch(createDining(formData));
      }

      if (response?.meta?.requestStatus === "fulfilled") {
        const targetID = DiningID || response?.payload?.Data?.ID;

        if (
          ((values?.Images && typeof values?.Images !== "string") ||
            values.attachments?.length > 0) &&
          targetID
        ) {
          const ImageData = new FormData();
          const Images =
            values?.Images && typeof values?.Images !== "string"
              ? getFile(values?.Images)
              : "";
          if (Images) {
            ImageData.append("Images", Images);
          }
          ImageData.append("ID", targetID.toString());

          if (values?.attachments && Array.isArray(values.attachments)) {
            values.attachments.forEach((image: any) => {
              if (image.attachment && typeof image.attachment !== "string")
                return;
              const attachment = getFile(image);
              if (attachment) {
                ImageData.append("ImageGallery", attachment);
              }
            });
          }

          await dispatch(DiningImageUpload(ImageData));
        }

        navigate("/dining");
        dispatch(clearDiningDetails());
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleDeleteImage = (ID: number) => {
    dispatch(openDialog("deleteDiningImage"));
    dispatch(setDiningImageID(ID));
  };

  const handleBack = () => {
    navigate("/dining");
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <DeleteDiningImageDialog />
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={
            DiningDetail || {
              DiningSpecialities: [{ DiningSpecialities: "" }],
              IsActive: 1,
            }
          }
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={4}>
                    <Typography.h4>
                      {DiningID ? "Update Dining" : "Add Dining"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="DiningTitle"
                      label="Title"
                      placeholder="Skyline Rooftop Cafe"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="DiningTagline"
                      label="Tagline"
                      placeholder="Dine Above the City Lights"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Timing"
                      label="Timing"
                      placeholder="5:00 PM - 12:30 AM"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Cuisine"
                      label="Cuisine"
                      placeholder="Italian, Mexican, Fusion"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", gap: "20px", width: "100%" }}
                  >
                    <Field
                      wrapperClassName="right-aligned-field"
                      name="Seating"
                      label="Seating"
                      placeholder="0"
                      component={FormTextField}
                    />
                    <Field
                      wrapperClassName="right-aligned-field"
                      name="SequenceNo"
                      label="Sequence No."
                      placeholder="0"
                      format="n0"
                      component={FormNumericTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => ({
                        value: status?.value,
                        label: status?.label,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="DiningDescription"
                      label="Dining Description"
                      component={FormRichTextField}
                      style={{ height: 150 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <FieldArray
                      name="DiningSpecialities"
                      component={DiningSpecialitiesArray}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      justifyContent="start"
                      name={`Images`}
                      label="Main Image"
                      type="file"
                      accept="image/*"
                      alternateName="Main Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      justifyContent="start"
                      name={`attachments`}
                      label="Inner Images"
                      type="file"
                      multiple
                      accept="image/*"
                      alternateName="Inner Images"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  {DiningDetail?.DiningInnerImage &&
                    DiningDetail?.DiningInnerImage?.length > 0 && (
                      <GridLayoutItem colSpan={4} className="mt-3">
                        <div className="card shadow-sm border-0">
                          {DiningDetail?.DiningInnerImage?.length > 0 && (
                            <div
                              className="card-body p-2 pt-3 pe-3"
                              style={{
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5
                                  className="mb-0"
                                  style={{
                                    color: "#2c3e50",
                                    fontWeight: "600",
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  Uploaded Images
                                </h5>
                                <span className="badge bg-kendo-primary rounded-pill">
                                  {DiningDetail?.DiningInnerImage?.length || 0}{" "}
                                  files
                                </span>
                              </div>

                              <div className="d-flex flex-wrap gap-3">
                                {DiningDetail?.DiningInnerImage?.map(
                                  (image: any) => (
                                    <div
                                      key={image.ID}
                                      className="position-relative"
                                      style={{
                                        width: "120px",
                                        transition: "all 0.3s ease",
                                      }}
                                    >
                                      <div className="card h-100 border-0 shadow-sm overflow-hidden">
                                        <div className="ratio ratio-1x1 bg-light overflow-hidden">
                                          <div
                                            style={{
                                              position: "absolute",
                                              width: "100%",
                                              height: "auto",
                                              display: "flex",
                                              justifyContent: "end",
                                            }}
                                          >
                                            <Button
                                              type="button"
                                              themeColor={"error"}
                                              fillMode={"solid"}
                                              style={{
                                                position: "relative",
                                                padding: "5px 10px",
                                                width: 30,
                                                height: 30,
                                                color: "#fff",
                                                zIndex: "1",
                                                borderRadius: "0 0 0 5px",
                                              }}
                                              onClick={() =>
                                                handleDeleteImage(image.ID)
                                              }
                                            >
                                              <IconComponent
                                                icon={MdDelete}
                                                className="absolute-position fs-5"
                                              />
                                            </Button>
                                          </div>
                                          <img
                                            className="img-fluid object-fit-cover"
                                            src={image.ImageGallery}
                                            alt="Uploaded document"
                                            style={{
                                              cursor: "pointer",
                                              transition: "transform 0.3s",
                                            }}
                                            onMouseEnter={(e) =>
                                              (e.currentTarget.style.transform =
                                                "scale(1.05)")
                                            }
                                            onMouseLeave={(e) =>
                                              (e.currentTarget.style.transform =
                                                "scale(1)")
                                            }
                                            onClick={() =>
                                              window.open(
                                                `${image.ImageGallery}`,
                                                "_blank",
                                              )
                                            }
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </GridLayoutItem>
                    )}

                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      textAlign: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={DiningID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={handleBack}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

const DeleteDiningImageDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const location = useLocation();
  const DiningID = location.state?.DiningID;

  const DiningImageID = useAppSelector((state) => state.dining.DiningImageID);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteDiningImage(ID));
      if (response.meta.requestStatus === "fulfilled") {
        if (DiningID) {
          dispatch(getDiningByID(DiningID));
        }
        dispatch(closeDialog());
        dispatch(clearDiningImageID());
      } else {
        if (DiningID) {
          dispatch(getDiningByID(DiningID));
        }
        dispatch(closeDialog());
        dispatch(clearDiningImageID());
      }
    }
  };
  return (
    <>
      {dialogName === "deleteDiningImage" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Image?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Image?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(DiningImageID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default CreateDining;
